import { useEffect, useState } from "react";
import { formatNumber, runSequence } from "shelving";
import { Frame, Frames, Icon, Number, type NumberProps } from "shared/react";
import { COUNTER_TYPES, type CounterData, type CounterType } from "shared/schema";
import { MEMORY } from "../util/db.js";

/** Watch changes to a counter on a user's profile, and display an animated `<NumberChangeNotice>` if that number changes. */
export function CounterWatcher({ uid, type }: { uid: string; type: CounterType }): React.ReactElement | null {
	const [element, setElement] = useState<React.ReactElement | null>(null);
	useEffect(() => {
		let lastCounter: CounterData | null = null;
		return runSequence(MEMORY.getCachedItemSequence("profile", uid), nextProfile => {
			if (nextProfile) {
				const nextCounter = nextProfile[type];
				if (lastCounter) {
					const from = lastCounter.total;
					const to = nextCounter.total;
					// const streakChange = nextCounter.streak - lastCounter.streak;
					if (from !== to) {
						const { icon, title, color } = COUNTER_TYPES[type];
						const props = {
							from,
							to,
							duration: 800,
							title: (
								<p className="space-none label flex-row flex-center gap-xsmall color-white">
									<Icon icon={icon} />
									<span>{title}</span>
								</p>
							),
							formatter: (num: number) => formatNumber(num, { maximumFractionDigits: 0, signDisplay: "never" }),
							mods: `fill-${color} color-white`,
						};
						setElement(
							<Frames key={from}>
								<Frame duration={10}>
									<CounterNotice paused {...props} mods={`${props.mods} push-right`} />
								</Frame>
								<Frame duration={500}>
									<CounterNotice paused {...props} />
								</Frame>
								<Frame duration={800}>
									<CounterNotice {...props} mods={`${props.mods} effect-excited`} />
								</Frame>
								<Frame duration={600}>
									<CounterNotice {...props} from={to} />
								</Frame>
								<Frame duration={300}>
									<CounterNotice paused {...props} from={to} mods={`${props.mods} push-left`} />
								</Frame>
							</Frames>,
						);
					}
				}
				lastCounter = nextCounter;
			}
		});
	}, []);
	return element;
}

export function CounterNotice({
	mods = "",
	title,
	from,
	to,
	...props
}: {
	title?: React.ReactNode;
	mods?: string | undefined;
} & NumberProps): React.ReactElement {
	return (
		<aside className={`pin-center layer-top width-quarters height-fit pad-2x fill-panel weight-bold text-center animate-fast ${mods}`}>
			{title}
			<p className="space-none weight-bold flex-row flex-center gap-xsmall">
				<Number prefix="+" from={to - from} to={0} {...props} mods="size-large" />
				<Icon icon="right" />
				<Number from={from} to={to} {...props} mods="size-3x" />
			</p>
		</aside>
	);
}
